// Palette — ported verbatim from the design spec's palette() function.
// Two base themes × four accent packs, emitted as CSS custom properties on the
// .ipsf root. Hex values live ONLY in this file; components always use var(--x).

export type ThemeMode = 'light' | 'dark' | 'system';
export type AccentName = 'Interpod Green' | 'Signal Blue' | 'Charcoal' | 'Hi-Vis';

export const ACCENT_NAMES: readonly AccentName[] = [
  'Interpod Green',
  'Signal Blue',
  'Charcoal',
  'Hi-Vis',
];

export interface Palette {
  bg: string;
  panel: string;
  surface: string;
  surface2: string;
  ink: string;
  sub: string;
  line: string;
  elev: string;
  pos: string;
  warn: string;
  danger: string;
  /**
   * Tinted grounds for the three state colours.
   *
   * Every status chip, tile and banner in the operator/leader screens is a
   * state colour on a wash of itself. They are tokens rather than inline
   * rgba() so the dark theme can lift them — the same 12% that reads as a
   * tint on white is invisible on #0e0f13.
   */
  posSoft: string;
  warnSoft: string;
  dangerSoft: string;
  stage: string;
  accent: string;
  accentInk: string;
  soft: string;
}

const DARK_BASE = {
  bg: '#0e0f13',
  panel: '#14161c',
  surface: '#191b21',
  surface2: '#23252d',
  ink: '#f3f5f7',
  sub: '#8a909a',
  line: '#2c2f38',
  elev: '0 10px 26px rgba(0,0,0,.45)',
  pos: '#37c77e',
  warn: '#f0a13a',
  danger: '#ff5a5f',
  posSoft: 'rgba(55,199,126,.20)',
  warnSoft: 'rgba(240,161,58,.20)',
  dangerSoft: 'rgba(255,90,95,.20)',
  stage: '#050609',
} as const;

const LIGHT_BASE = {
  bg: '#f0f1f4',
  panel: '#ffffff',
  surface: '#ffffff',
  surface2: '#eceef2',
  ink: '#16181c',
  sub: '#6c717a',
  line: '#e3e6ea',
  elev: '0 6px 20px rgba(20,22,25,.08)',
  pos: '#1f9d57',
  warn: '#c0791f',
  danger: '#d64550',
  posSoft: 'rgba(31,157,87,.12)',
  warnSoft: 'rgba(192,121,31,.12)',
  dangerSoft: 'rgba(214,69,80,.12)',
  stage: '#d7d9de',
} as const;

function accentPack(name: AccentName, dark: boolean): Pick<Palette, 'accent' | 'accentInk' | 'soft'> {
  switch (name) {
    case 'Signal Blue':
      return {
        accent: '#2f6bff',
        accentInk: '#ffffff',
        soft: dark ? 'rgba(47,107,255,.20)' : 'rgba(47,107,255,.12)',
      };
    case 'Charcoal':
      // The only pack that swaps with the theme.
      return dark
        ? { accent: '#e7e9ec', accentInk: '#16181c', soft: 'rgba(255,255,255,.10)' }
        : { accent: '#2b2e33', accentInk: '#ffffff', soft: 'rgba(43,46,51,.10)' };
    case 'Hi-Vis':
      return {
        accent: '#c6f24e',
        accentInk: '#15170a',
        soft: dark ? 'rgba(198,242,78,.18)' : 'rgba(120,150,20,.14)',
      };
    case 'Interpod Green':
    default:
      return {
        accent: '#6e7367',
        accentInk: '#ffffff',
        soft: dark ? 'rgba(144,146,137,.22)' : 'rgba(110,115,103,.14)',
      };
  }
}

export function isDark(theme: ThemeMode, sysDark: boolean): boolean {
  return theme === 'dark' || (theme === 'system' && sysDark);
}

export function buildPalette(theme: ThemeMode, accent: AccentName, sysDark: boolean): Palette {
  const dark = isDark(theme, sysDark);
  return { ...(dark ? DARK_BASE : LIGHT_BASE), ...accentPack(accent, dark) };
}

/** Palette → the inline style object that declares every CSS variable. */
export function paletteVars(p: Palette): Record<string, string> {
  return {
    '--bg': p.bg,
    '--panel': p.panel,
    '--surface': p.surface,
    '--surface2': p.surface2,
    '--ink': p.ink,
    '--sub': p.sub,
    '--line': p.line,
    '--elev': p.elev,
    '--pos': p.pos,
    '--warn': p.warn,
    '--danger': p.danger,
    '--posSoft': p.posSoft,
    '--warnSoft': p.warnSoft,
    '--dangerSoft': p.dangerSoft,
    '--stage': p.stage,
    '--accent': p.accent,
    '--accentInk': p.accentInk,
    '--soft': p.soft,
  };
}
